import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import MenuButtonsPortal from '../Menu/MenuButtonsPortal';
import BiButton from '../primitives/buttons/BiButton.primitive';
import LoaderContainer from '../shared/loaderContainer/LoaderContainer';
import { ITableHeader } from '../shared/tables/models/ITableHeader';
import TemplateTable from '../shared/tables/TemplateTable';
import { TableContainer } from '../Organizations/OrganizationsList';
import { getApplications, deleteApplication } from '../../http/metadata-service';
import { HTTP_STATUS } from '../../http/constants/http.status';
import { AppConstants } from '../../constants/AppConstants';
import { useTheme } from '../../context/themeContext';
import { setBreadcrump } from '../../app/organizationReducer';
import { useNavigate } from 'react-router-dom';
import { plusSign } from '../../assets/components/svgs';
import { setApplication } from '../../app/metadataReducer';
import { setToastData } from '../../app/toastReducer';
import { setCareplanConfig, setCareplanData, setOrgList } from '../../app/carePlanReducer';
import { noDataAvailableMessage } from '../Organizations/helpers';
import {
  cloneDeep,
  debounce,
  findIndex,
  capitalize,
  filter,
  map,
  find,
  uniqBy,
  isEmpty
} from 'lodash';
import { getFailureMessage } from '../../util/ErrorUtil';
import { RouteConstants } from '../../constants/RouteConstants';
// import DeleteConfirmModal from './DeleteConfirmModal';
import { EllipsisDiv } from '../../styles/globalStyles';
import Checkbox from '../shared/Fields/Checkbox';
import { deleteManifestItem, setManifest } from '../../app/migrationExportReducer';
import { ActionButtons } from '../Reusable/ActionButton';
import AddCircle from '../../assets/addCircle.svg';
import { getCarePlanTemplates, deleteCareplan } from '../../http/careplan-services';
import { useDidMountEffect } from '../../util/customHooks';

import { updateSelectedFilter } from '../ConfigurationService/Configutils';

import { setTableOptions } from '../../app/templateTableReducer';

import {
  mergeConditionsCareTeams,
  getOrgForCarePlans,
  filterOrgNames,
  getOrgList,
  configureCareplanCategories
} from './carePlanUtils';
import { MenuConstants } from '../../constants/MenuConstants';
import { MenuOptions } from '../primitives/dotMenu/ContextMenu';
import Modal from '../shared/ModalPortal/Modal';
import { ExclamationIcon } from '@heroicons/react/outline';
import { checkFeaturePermission } from '../../util/permissionUtils';
import moment from 'moment';
import CareplanMenu from './CareplanMenu';

/**
 * @parentId careplan
 * @manager Careplan Listing
 * @overview Lists Careplan Templates
 *   <section>
 *       <p>
 *          Careplan listing page list all the Careplan templates. User can navigate to Careplan listing page by clicking <b>Care Plan Template</b> navigation menuitem under the <strong>Platform Core</strong> menu group of admin portal left sidebar.
 *          The Default pagination size be fixed to 20 items, which can be changed to 50 or 100 from the dropdown menu of datagrid footer (The paging options are defined from the configuration). Datagrid footer will also display the current page and total number of pages available based on the choosen page size & Careplan(s) data.
 *       </p>
 *       <br>
 *       <p>
 *          Careplans can be filtered based on Status, Title, Intent and Organization. Admin user(s) can create new Careplan by clicking on <b>New Template</b> button provided on the admin portal toolbar,
 *           which will navigate user to Careplan creation page. <b>New Template</b> button will be shown based on logged in user role API permissions as described below.
 *       </p>
 *       <br>
 *       <p>
 *           Datagrid will display details of each careplan as row item with each column representing below object property.
 *           <br>
 *           <ul>
 *               <li>Status</li>
 *               <li>Title</li>
 *               <li>Intent</li>
 *               <li>Organization</li>
 *           </ul>
 *       </p>
 *       <p>
 *           Sorting of table grid is only supported on <i>Status</i>, <i>Title</i> and <i>Intent</i> columns.
 *       </p>
 *     </section>
 *      <section>
 *         <p>
 *          <h4>Table RowItem Dot Menu Options</h4>
 *          Careplan listing table will have the following options in individual row items:
 *          <br/>
 *          <table>
 *            <tr>
 *               <th>Name</th>
 *               <th>Description</th>
 *               <th>Permission Needed</th>
 *            </tr>
 *              <tr>
 *               <td>View Careplan Activities</td>
 *               <td>Navigates to Activities Listing Page for the Careplan template</td>
 *               <td>careplan-service.careplan.activity.list</td>
 *            </tr>
 *             <tr>
 *               <td>Add an activity</td>
 *               <td>Navigates to Create Activity Page for the Careplan template</td>
 *               <td>careplan-service.careplan.activity.add</td>
 *            </tr>
 *             <tr>
 *               <td>View Notes</td>
 *               <td>Navigates to Notes Listing Page for the Careplan Template.</td>
 *               <td>careplan-service.careplan.notes.list</td>
 *            </tr>
 *            <tr>
 *               <td>Add a Note</td>
 *               <td>Navigates to Create Note Page for the Careplan Template.</td>
 *               <td>careplan-service.careplan.notes.create</td>
 *            </tr>
 *             <tr>
 *               <td>Delete</td>
 *               <td>Deletes the Careplan template. On selection a confirmation popup appears.</td>
 *               <td>careplan-service.careplan.delete</td>
 *            </tr>
 *          </table>
 *          <br/>
 *        </p>
 *      </section>
 *       <section>
 *         <p>
 *          <h4>Supported Filters</h4>
 *          Careplan listing table will have the following filter options:
 *          <br/>
 *          <table>
 *            <tr>
 *               <th>Name</th>
 *               <th>Type</th>
 *               <th>Description</th>
 *            </tr>
 *              <tr>
 *               <td>Status</td>
 *               <td>Dropdown</td>
 *               <td>Used to filter based on the Status Field</td>
 *            </tr>
 *             <tr>
 *               <td>Title</td>
 *               <td>Text Field</td>
 *               <td>This is an exact search which filters data based on exact title.</td>
 *            </tr>
 *             <tr>
 *               <td>Intent</td>
 *               <td>Dropdown</td>
 *               <td>Used for filtering based on Intent.</td>
 *            </tr>
 *               <tr>
 *               <td>Organizations</td>
 *               <td>Dropdown</td>
 *               <td>Used for filtering based on organizations.</td>
 *            </tr>
 *          </table>
 *          <br/>
 *        </p>
 *      </section>
 *       <section>
 *         <p>
 *          <h4>Configuration based options</h4>
 *          Careplan dropdown options in filters are configured via the System Setting with category "careplan.template.default.values". User can configure these options in the system settings for the following fields in listing:
 *        <br/>
 *          <table>
 *            <tr>
 *               <th>Name</th>
 *               <th>Key</th>
 *            </tr>
 *              <tr>
 *               <td>Status</td>
 *               <td>careplan.template.status.values</td>
 *            </tr>
 *             <tr>
 *               <td>Intent</td>
 *               <td>careplan.template.intent.values</td>
 *            </tr>
 *          </table>
 *          <br/>
 *        </p>
 *      </section>
 *     <section>
 *      <h4>Failure Status Codes</h3>
 *       <p>
 *       This section describes the List Careplan Status Code information.
 *       <br>
 *       <table>
 *           <tr>
 *               <th>HTTP Status Code</th>
 *               <th>Service Error Code</th>
 *               <th>Error Message</th>
 *           </tr>
 *           <tr>
 *               <td>403</td>
 *               <td>CPS_PERMISSION_DENIED</td>
 *               <td>You do not have permission to view this page</td>
 *           </tr>
 *           <tr>
 *               <td>500</td>
 *               <td>CPS_INTERNAL_ERROR</td>
 *               <td>Internal Server Error</td>
 *           </tr>
 *           <tr>
 *               <td>503</td>
 *               <td></td>
 *               <td>Service Unavailable</td>
 *           </tr>
 *       </table>
 *   </p>
 *   </section
 *   <section>
 *      <h4>Dependent System settings, Platform services & Role Permission</h3>
 *      <p>This section describes the list of dependent system settings & platform services required for functioning of careplan listing page.</p>
 *       <h5>System Settings</h4>
 *       <p>Table lists all the dependent system setting(s) defined in configuration service with either global/organization scope</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>Key</th>
 *               <th>Type</th>
 *               <th>Value</th>
 *               <th>Scope</th>
 *           </tr>
 *           <tr>
 *               <td>None</td>
 *               <td>None</td>
 *               <td>None</td>
 *               <td>None</td>
 *           </tr>
 *       </table>
 *       <br>
 *      <h5>Platform Service(s)</h4>
 *      <p>Table lists all the dependent platform service(s) with specific version(s) for careplan listing</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>Service Name</th>
 *               <th>Version</th>
 *           </tr>
 *           <tr>
 *               <td>Careplan Service</td>
 *               <td>1.3.0</td>
 *           </tr>
 *           <tr>
 *               <td>User Management Service</td>
 *               <td>1.6.0</td>
 *           </tr>
 *           <tr>
 *               <td>Configuration Service</td>
 *               <td>1.6.0</td>
 *         </tr>
 *       </table>
 *       <br>
 *      <h5>API Role Permission(s)</h4>
 *      <p>Table lists the required API role permissions for careplan listing page</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>API URL</th>
 *               <th>API Method</th>
 *               <th>API Permission</th>
 *               <th>Required</th>
 *           </tr>
 *           <tr>
 *               <td>/careplans</td>
 *               <td>GET</td>
 *               <td>careplan-service.careplan.list</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>/careplans</td>
 *               <td>DELETE</td>
 *               <td>careplan-service.careplan.delete</td>
 *               <td>No</td>
 *           </tr>
 *           <tr>
 *               <td>/careplans/:id/activities</td>
 *               <td>POST</td>
 *               <td>careplan-service.careplan.activity.add</td>
 *               <td>No</td>
 *           </tr>
 *           <tr>
 *               <td>/careplans/:id/activities</td>
 *               <td>GET</td>
 *               <td>careplan-service.careplan.activity.list</td>
 *               <td>No</td>
 *           </tr>
 *             <tr>
 *               <td>/careplans/:id/notes</td>
 *               <td>POST</td>
 *               <td>careplan-service.careplan.notes.create</td>
 *               <td>No</td>
 *            </tr>
 *            <tr>
 *               <td>/careplans/:id/notes</td>
 *               <td>GET</td>
 *               <td>careplan-service.careplan.notes.list</td>
 *               <td>No</td>
 *            </tr>
 *       </table>
 *   </section>
 *   <section>
 *   <section>
 *    <p>Sequence Diagram for Careplan listing page</p>
 *   </section>
 *   </section>
 */

interface CarePlanTemplateListProps {
  type?: 'listing' | 'migrationSelection' | 'migrationPreview';
  toggleModal?: Function;
}

const CarePlanTemplateList = ({
  type = 'listing',
  toggleModal = () => {}
}: CarePlanTemplateListProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [rows, setRows] = useState<any>([]);
  const [paging, setPaging] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentActivePage, setCurrentActivePage] = useState(0);
  const [deletePopup, setDeletePopup] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<string>('');
  const [configLoaded, setConfigLoaded] = useState(false);

  const userProfileInfo: any = useAppSelector((state) => state.userManagement.userProfileInfo);

  const globalFilters: any = useAppSelector((state) => state.templateTableReducer.tableOptions);

  const conditionsListData: any =
    useAppSelector((state) => state.carePlanData.conditionsList) || [];

  const carePlanOrgList: any = useAppSelector((state) => state.carePlanData.orgList) || [];
  const careplanConfig: any = useAppSelector((state) => state.carePlanData.careplanConfig) || {};

  const userProfile = useAppSelector((state) => state.userManagement.userProfileInfo);

  const [selectedItems, setSelectedItems] = useState<any>([]);

  const manifestCarePlansData = useAppSelector(
    (state: any) => state.migrationExport?.manifest?.careplans
  );

  const [loadNextData, setLoadNextData] = useState<any>({
    orgPaging: false,
    searchOrgName: '',
    searchOrg: false,
    searchCareTeamName: '',
    searchCareTeam: false,
    searchConditionName: '',
    searchCondition: false
  });

  const checkConfiguration = async () => {
    let config = await configureCareplanCategories();
    if (!isEmpty(config)) {
      dispatch(setCareplanConfig(config));
    } else {
      setConfigLoaded(true);
    }
  };

  useEffect(() => {
    dispatch(
      setBreadcrump([
        {
          key: RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_LIST,
          label: `${t('T_CARE_PLAN_TEMPLATES')}`
        }
      ])
    );
    if (type === 'migrationPreview') {
      setIsLoading(false);
      return;
    }

    if (type !== 'listing') {
      dispatch(
        setTableOptions({
          'careplan-list': {
            searchText: '',
            currentPage: AppConstants.DEFAULT_PAGE,
            size: AppConstants.MODULE_LIST_DEFAULT_PAGE_SIZE,
            sortName: '',
            sortType: '',
            tableFilters: { orgFilter: [] }
          }
        })
      );
    }
  }, []);

  // const getCarePlanConditions = async () => {
  //   const queryOptions = { size: AppConstants.MAXIMUM_PAGE_SIZE };
  //   const conditionResponse = await getConditions(queryOptions);

  //   const { status, data: conditionsData } = conditionResponse;
  //   if (status === HTTP_STATUS.HTTP_OK) {
  //     const { data: conditions = [], paging } = conditionsData;
  //     if (conditions.length) {
  //       let filteredData = conditionsListData.length
  //         ? uniqBy([...conditionsListData, ...conditions], 'id')
  //         : [...conditions];
  //       dispatch(setConditionsList(filteredData));
  //       return filteredData;
  //     }
  //   }
  // };

  // const getCareTeamsList = async () => {
  //   const queryOptions = { size: AppConstants.MAXIMUM_PAGE_SIZE };
  //   const careTeamResponse = await getCareTeams(queryOptions);

  //   const { status, data: careTeamsData } = careTeamResponse;
  //   if (status === HTTP_STATUS.HTTP_OK) {
  //     const { data: careTeams = [], paging } = careTeamsData;
  //     if (careTeams.length) {
  //       let filtered = careTeamsListData.length
  //         ? uniqBy(filterCareTeams([...careTeamsListData, ...careTeams]), 'id')
  //         : filterCareTeams([...careTeams]);
  //       dispatch(setCareTeamsList(filtered));
  //       return filtered;
  //     }
  //   }
  // };

  const loadCarePlanTemplates = async ({ ...options }) => {
    const { page, size, search = '', sortOrder = '', sortType = '', filters = {} } = options;
    const response = await getCarePlanTemplates(
      page,
      size,
      sortType ? [`${sortOrder},${sortType}`] : [],
      filters
    );
    const { status, data: careTemplateResponse } = response;
    if (status === HTTP_STATUS.HTTP_OK) {
      const { data: templates = [], paging } = careTemplateResponse;
      if (templates.length) {
        const updatedTemplateData = mergeConditionsCareTeams(
          templates,
          conditionsListData || [],
          [], // careTeamsListData || [],
          carePlanOrgList || []
        );
        //setRows([...templates]);
        setRows([...updatedTemplateData]);
        setPaging(paging);
      } else {
        setRows(`${t('T_NO_DATA_AVAILABLE_MSG')}`);
      }
    } else {
      dispatch(
        setToastData({
          toastMessage: getFailureMessage(response),
          isToastActive: true,
          toastType: 'error'
        })
      );
    }
    setIsLoading(false);
  };

  const tableHeadersBuilder = () => {
    let TABLE_HEADERS: Array<ITableHeader> = [
      {
        key: 'title',
        description: 'T_TITLE',
        sortEnabled: type !== 'migrationPreview',
        clickable: type === 'listing',
        sortingInformation: {
          order: ''
        },
        styles: { borderRight: type === 'listing' ? 'none' : '' },
        render: (data: any) => {
          return (
            <EllipsisDiv className="w-full flex-1" title={data?.title}>
              {data?.title}
            </EllipsisDiv>
          );
        },
        width: type === 'listing' ? '30%' : type === 'migrationPreview' ? '80%' : '90%'
      },
      {
        key: 'status',
        description: 'T_STATUS',
        sortEnabled: type !== 'migrationPreview',
        clickable: type === 'listing',
        sortingInformation: {
          order: ''
        },
        render: (row: any) => {
          return capitalize(row?.status);
        },
        width: '10%'
      }
    ];

    switch (type) {
      case 'listing':
        TABLE_HEADERS.splice(1, 0, {
          key: 'options',
          description: '',
          sortEnabled: false,
          width: '7%',
          clickable: false,
          sortingInformation: {
            order: ''
          },
          nastedData: true,
          render: (data: any) => {
            return (
              <div className="hover:bg-[#d9d9d9]" style={{ borderRadius: '10px' }}>
                <CareplanMenu
                  className="px-0 py-0"
                  onOptionClick={(key: string) => contextMenuHandler(key, data)}
                />
              </div>
            );
          },
          styles: {
            paddingRight: '0',
            paddingLeft: '0',
            borderLeft: 'none',
            rowStyle: { fontWeight: 'normal', padding: '0px' }
          }
        });
        TABLE_HEADERS = TABLE_HEADERS.concat([
          {
            key: 'intent',
            description: 'T_CARE_PLAN_INTENT',
            sortEnabled: true,
            clickable: true,
            sortingInformation: {
              order: ''
            },
            render: (data: any) => {
              return (
                <EllipsisDiv className="w-full flex-1" title={data?.intent}>
                  {capitalize(data?.intent)}
                </EllipsisDiv>
              );
            },
            width: type === 'listing' ? '20%' : type === 'migrationPreview' ? '35%' : '40%'
          },
          // {
          //   key: 'conditions',
          //   description: 'T_CONDITIONS',
          //   sortEnabled: false,
          //   clickable: false,
          //   sortingInformation: {
          //     order: ''
          //   },
          //   render: (data: any) => {
          //     return (
          //       <EllipsisDiv className="w-full flex-1" title={data?.conditions}>
          //         {capitalize(data?.conditions)}
          //       </EllipsisDiv>
          //     );
          //   },
          //   width: type === 'listing' ? '30%' : type === 'migrationPreview' ? '35%' : '40%'
          // },
          // {
          //   key: 'careTeams',
          //   description: 'T_CARETEAMS',
          //   sortEnabled: false,
          //   clickable: false,
          //   sortingInformation: {
          //     order: ''
          //   },
          //   render: (data: any) => {
          //     return (
          //       <EllipsisDiv className="w-full flex-1" title={data?.careTeams}>
          //         {capitalize(data?.careTeams)}
          //       </EllipsisDiv>
          //     );
          //   },
          //   width: type === 'listing' ? '30%' : type === 'migrationPreview' ? '35%' : '40%'
          // },
          {
            key: 'orgData',
            description: 'T_ORGANIZATION',
            sortEnabled: false,
            clickable: true,
            sortingInformation: {
              order: ''
            },
            render: (data: any) => {
              return (
                <EllipsisDiv className="w-full flex-1" title={data?.orgList}>
                  {capitalize(data?.orgList)}
                </EllipsisDiv>
              );
            },
            width: type === 'listing' ? '25%' : type === 'migrationPreview' ? '35%' : '40%'
          },
          {
            key: 'updatedOn',
            description: 'T_LAST_MODIFIED_ON',
            sortEnabled: false,
            clickable: true,
            render: (data: any) => {
              return data.updatedOn
                ? moment(data.updatedOn).format(AppConstants.DEFAULT_DATE_FORMAT)
                : '';
            },
            width: '15%',
            sortingInformation: {
              order: ''
            }
          }
        ]);
        break;
      case 'migrationSelection':
        TABLE_HEADERS.unshift({
          key: 'checked',
          description: '',
          sortEnabled: false,
          width: '10%',
          sortingInformation: {
            order: ''
          },
          nastedData: true,
          render: (data: any, index: any) => {
            return (
              <>
                <Checkbox
                  className="mx-auto"
                  height="20px"
                  width="20px"
                  checked={isCheckboxSelected(data)}
                  onChange={() => {
                    toggleCheckbox(data);
                  }}
                  testID="addModuleCheckbox"
                />
              </>
            );
          }
        });
        break;
      case 'migrationPreview':
        TABLE_HEADERS.push(
          {
            key: 'overWrite',
            description: 'T_CONFIG_KEY_ALLOW_OVERRIDE',
            sortEnabled: false,
            width: '10%',
            sortingInformation: {
              order: ''
            },
            nastedData: true,
            render: (data: any, index: any) => {
              return (
                <>
                  <Checkbox
                    className="mx-auto"
                    height="20px"
                    width="20px"
                    onChange={() => {
                      toggleOverWrite(data);
                    }}
                    checked={data?.overWrite}
                    testID="allowOverWriteCheckbox"
                  />
                </>
              );
            }
          },
          {
            key: 'buttons',
            description: t('T_ACTIONS'),
            sortEnabled: false,
            width: '10%',
            sortingInformation: {
              order: ''
            },
            styles: {
              justifyContent: 'center'
            }
          }
        );
        break;
    }

    return TABLE_HEADERS;
  };

  const deleteCareplanAPI = async () => {
    setIsLoading(true);
    let response = await deleteCareplan(deleteId);

    if ([HTTP_STATUS.HTTP_CREATED, HTTP_STATUS.HTTP_OK].includes(response.status)) {
      let ele = find(rows, { id: deleteId });
      callWithFilters();
      setDeleteId('');

      dispatch(
        setToastData({
          toastMessage: t('T_CARE_PLAN_DELETED_SUCCESS').replace('%s', ele?.title || ''),
          isToastActive: true,
          toastType: 'success'
        })
      );
    } else {
      setDeleteId('');
      setIsLoading(false);
      dispatch(
        setToastData({
          toastMessage: getFailureMessage(response),
          isToastActive: true,
          toastType: 'error'
        })
      );
    }
  };

  const contextMenuHandler = (key: string, data: any) => {
    if (key === MenuConstants.MENU_CARE_PLAN_NOTES_CREATE) {
      dispatch(setCareplanData(data));
      navigate(`${RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_NOTES_CREATE.replace(':id', data.id)}`);
    }

    if (key === MenuConstants.MENU_CARE_PLAN_ACTIVITIES_CREATE) {
      dispatch(setCareplanData({ ...data }));
      navigate(RouteConstants.ROUTE_CARE_PLAN_ACTIVITIES_CREATE.replace(':id', data.id));
    }

    if (key === MenuConstants.MENU_CARE_PLAN_ACTIVITIES) {
      dispatch(setCareplanData({ ...data }));
      navigate(RouteConstants.ROUTE_CARE_PLAN_ACTIVITIES.replace(':id', data.id));
    }
    if (key === MenuConstants.MENU_CARE_PLAN_NOTES) {
      dispatch(setCareplanData(data));
      navigate(`${RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_NOTES_LIST.replace(':id', data.id)}`);
    }
  };

  const [tableHeaders, setTableHeaders] = useState(tableHeadersBuilder());

  useEffect(() => {
    if (manifestCarePlansData?.length) {
      setSelectedItems(manifestCarePlansData);
    }
  }, [manifestCarePlansData]);

  useEffect(() => {
    if (type === 'migrationSelection') {
      setTableHeaders(tableHeadersBuilder());
    } else if (type === 'migrationPreview') {
      setRows(selectedItems);
      setIsLoading(false);
      setTableHeaders(tableHeadersBuilder());
    }
  }, [selectedItems]);

  const isCheckboxSelected = (data: any) => {
    const { id } = data;

    return (
      findIndex(selectedItems, function (item: any) {
        return item.id === id;
      }) !== -1
    );
  };

  const toggleCheckbox = (data: any) => {
    const { id, status = '', title = '' } = data;

    let existingArr: any = cloneDeep(selectedItems);
    let existingId = findIndex(existingArr, function (item: any) {
      return item.id === id;
    });

    if (existingId !== -1) {
      existingArr.splice(existingId, 1);
    } else {
      existingArr.push({ id, status, title });
    }

    setSelectedItems(existingArr);
  };

  const toggleOverWrite = (data: any) => {
    const { id } = data;

    let existingArr: any = cloneDeep(manifestCarePlansData);
    let existingId = findIndex(existingArr, function (item: any) {
      return item.id === id;
    });

    if (existingId !== -1) {
      if (existingArr[existingId]?.overWrite) {
        delete existingArr[existingId].overWrite;
      } else {
        existingArr[existingId].overWrite = true;
      }

      dispatch(setManifest({ key: 'careplans', value: existingArr }));
      return;
    }
  };

  const fetchMoreOrgData = () => {
    setLoadNextData((prevState: any) => ({ ...prevState, orgPaging: true }));
  };

  const searchOrgData = (data: any) => {
    setLoadNextData((prevState: any) => ({
      ...prevState,
      searchOrgName: data,
      searchOrg: true,
      searchCareTeam: false,
      searchCondition: false
    }));
  };

  const searchCareTeam = (data: any) => {
    setLoadNextData((prevState: any) => ({
      ...prevState,
      searchCareTeamName: data,
      searchCareTeam: true,
      searchOrg: false,
      searchCondition: false
    }));
  };

  const searchConditions = (data: any) => {
    setLoadNextData((prevState: any) => ({
      ...prevState,
      searchConditionName: data,
      searchCondition: true,
      searchOrg: false,
      searchCareTeam: false
    }));
  };

  const carePlanListFilters = [
    {
      id: 'status',
      label: 'T_FILTER_KEY_STATUS',
      component: 'picklist',
      type: 'multiple',
      itemsList: careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_STATUS_VALUES],
      selectedFilter: [],
      fieldKey: 'key',
      fieldValues: 'display',
      disabled: false,
      hasMore: false,
      paging: { size: 25, page: 1 }
    },
    {
      id: 'titleFilter',
      label: 'T_FILTER_KEY_TITLE',
      component: 'input',
      type: 'text',
      itemsList: [],
      selectedFilter: [],
      fieldKey: 'carePlanTitle',
      disabled: false,
      searchable: true,
      searchPlaceholder: 'Search by title',
      onlySearch: true
    },
    {
      id: 'intentFilter',
      label: 'T_FILTER_KEY_INTENT',
      component: 'picklist',
      type: 'multiple',
      itemsList: careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_INTENT_VALUES],
      selectedFilter: [],
      fieldKey: 'key',
      fieldValues: ['display'],
      disabled: false,
      hasMore: false
    },
    {
      id: 'orgFilter',
      label: 'T_FILTER_KEY_ORGANIZATIONS',
      component: 'picklist',
      type: type === 'listing' ? 'multiple' : 'single',
      itemsList: [],
      selectedFilter: [],
      fieldKey: 'name',
      fieldValues: 'name',
      disabled: false,
      hasMore: false,
      paging: { size: 1000, page: 1 },
      searchable: true
    },
    // {
    //   id: 'conditions',
    //   label: 'T_FILTER_KEY_CONDITIONS',
    //   component: 'picklist',
    //   type: 'multiple',
    //   itemsList: [],
    //   selectedFilter: [],
    //   fieldKey: 'key',
    //   fieldValues: 'value',
    //   disabled: false,
    //   hasMore: false,
    //   paging: { size: 25, page: 1 },
    //   searchable: false
    // },
    // {
    //   id: 'careTeamFilter',
    //   label: 'T_FILTER_KEY_CARETEAMS',
    //   component: 'picklist',
    //   type: 'multiple',
    //   itemsList: [],
    //   selectedFilter: [],
    //   fieldKey: 'id',
    //   fieldValues: 'displayVal',
    //   disabled: false,
    //   hasMore: false,
    //   paging: { size: 1000, page: 1 },
    //   searchable: true
    // },

    {},
    {}
  ];

  const [paramObj, setParamObj] = useState({
    orgFilter: '',
    status: ''
  });

  const serachFuncMapping: any = {
    orgFilter: searchOrgData,
    careTeamFilter: searchCareTeam,
    conditions: searchConditions
  };

  const carePlanFiltersList = carePlanListFilters.map((filter: any) => {
    return {
      ...filter,
      fetchMoreData: fetchMoreOrgData,
      searchData: serachFuncMapping[filter.id]
    };
  });

  const [filterList, setFilterList] = useState<Array<any>>(carePlanFiltersList);
  const [filterPillsData, setFilterPillsData] = useState<Array<any>>([]);

  const callWithFilters = () => {
    const {
      tableFilters = {},
      searchText = '',
      currentPage = AppConstants.DEFAULT_PAGE,
      size = AppConstants.DEFAULT_PAGE_SIZE,
      sortName = AppConstants.DEFAULT_SORT_BY,
      sortType = AppConstants.DEFAULT_SORT_ORDER
    } = globalFilters['careplan-list'] || {};
    applyFilters(searchText, currentPage, size, sortName, sortType, tableFilters);
  };

  useEffect(() => {
    const init = async () => {
      // let [carePlanConditions, carePlanTeams] = await Promise.all([
      //   getCarePlanConditions(),
      //   getCareTeamsList()
      // ]);

      // await setFilterData(carePlanConditions, carePlanTeams);
      await setFilterData([], [], careplanConfig);
    };

    if (type !== 'migrationPreview') {
      if (isEmpty(careplanConfig) && !configLoaded) {
        checkConfiguration();
      } else if (!isEmpty(careplanConfig) || configLoaded) {
        init();
      }
    }
  }, [careplanConfig, configLoaded]);

  useDidMountEffect(() => {
    if (type !== 'migrationPreview') {
      callWithFilters();
    }
  }, [globalFilters]);

  useDidMountEffect(async () => {
    if (loadNextData.searchOrg) {
      loadOrgSearchData();
    }

    // if (loadNextData.searchCareTeam) {
    //   loadCareTeamSearchData();
    // }

    // if (loadNextData.searchCondition) {
    //   loadConditionSearchData();
    // }
  }, [loadNextData]);

  const loadOrgSearchData = () => {
    const orgResponse = filterOrgNames(loadNextData.searchOrgName, carePlanOrgList || []);
    updateFilterSetData(orgResponse, careplanConfig, conditionsListData, []);
    updateSelectedFilter(
      filterList,
      filterPillsData,
      '',
      fetchMoreOrgData,
      setFilterPillsData,
      () => {} //setFilterList
    );
  };

  // const loadCareTeamSearchData = () => {
  //   const careTeamFilterResponse = filterCareTeamNames(
  //     loadNextData.searchCareTeamName,
  //     careTeamsListData || []
  //   );
  //   updateFilterSetData(carePlanOrgList, conditionsListData, careTeamFilterResponse);
  //   updateSelectedFilter(
  //     filterList,
  //     filterPillsData,
  //     '',
  //     fetchMoreOrgData,
  //     setFilterPillsData,
  //     () => {} //setFilterList
  //   );
  // };

  // const loadConditionSearchData = () => {
  //   const conditionFilterResp = filterConditionNames(
  //     loadNextData.searchConditionName,
  //     conditionsListData || []
  //   );
  //   updateFilterSetData(carePlanOrgList, conditionFilterResp, careTeamsListData);
  //   updateSelectedFilter(
  //     filterList,
  //     filterPillsData,
  //     '',
  //     fetchMoreOrgData,
  //     setFilterPillsData,
  //     () => {} //setFilterList
  //   );
  // };

  const updateFilterSetData = (
    orgResponse: any,
    careplanConfig: any = {},
    carePlanConditions: any = [],
    carePlanTeams: any = []
  ) => {
    let initialFilterList: any = [...filterList];

    const currentFilterList = initialFilterList.map((obj: any) => {
      if (obj.id === 'orgFilter') {
        return {
          ...obj,
          itemsList: getOrgList(orgResponse)
        };
      } else if (obj.id === 'status') {
        return {
          ...obj,
          itemsList: careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_STATUS_VALUES]
        };
      } else if (obj.id === 'intentFilter') {
        return {
          ...obj,
          itemsList: careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_INTENT_VALUES]
        };
      }
      // else if (obj.id === 'conditions') {
      //   return {
      //     ...obj,
      //     itemsList: getConditionsList(carePlanConditions)
      //   };
      // }
      // else if (obj.id === 'careTeamFilter') {
      //   return {
      //     ...obj,
      //     itemsList: getFilterCareTeamsList(carePlanTeams)
      //   };
      // }
      else {
        return { ...obj };
      }
    });
    setFilterList(currentFilterList);
  };

  const setFilterData = async (
    carePlanConditions: any = [],
    carePlanTeams: any = [],
    config = {}
  ) => {
    let orgResponse = await getOrgForCarePlans(loadNextData, filterList, userProfileInfo);

    updateFilterSetData(orgResponse, config, carePlanConditions, carePlanTeams);

    if (orgResponse?.length) {
      dispatch(
        setOrgList(
          carePlanOrgList.length
            ? uniqBy([...carePlanOrgList, ...orgResponse], 'id')
            : [...orgResponse]
        )
      );
    }

    const {
      tableFilters = {},
      searchText = '',
      currentPage: filterCurrentPage = AppConstants.DEFAULT_PAGE,
      size: filterSize = type === 'listing'
        ? AppConstants.DEFAULT_PAGE_SIZE
        : AppConstants.MODULE_LIST_DEFAULT_PAGE_SIZE,
      sortName = '',
      sortType = ''
    } = globalFilters['careplan-list'] || {};

    dispatch(
      setTableOptions({
        ['careplan-list']: {
          searchText: searchText,
          currentPage: filterCurrentPage,
          size: filterSize,
          sortName: sortName,
          sortType: sortType,
          tableFilters: tableFilters
        }
      })
    );
  };

  const applyFilters = async (
    search: string = '',
    page: number,
    size: number,
    sortOrder: string = '',
    sortType: string = '',
    filters: any
  ) => {
    setIsLoading(true);
    const appliedFilterList = Object.keys(filters);
    setFilterPillsData(updatePillsData(filters, appliedFilterList));
    setFilterList(updateFilterListSelected(filters));
    setParamObj(updateParamObj(filters, appliedFilterList));

    loadCarePlanTemplates({ page, size, search, sortOrder, sortType, filters });
  };

  const updateFilterListSelected = (appliedFilters: any) => {
    const initialFilterList: any = [...filterList];
    const updatedFilterList = initialFilterList.map((filterObj: any) => {
      const selectedFilter = appliedFilters[filterObj.id] ? appliedFilters[filterObj.id] : [];
      return { ...filterObj, selectedFilter };
    });

    return updatedFilterList;
  };

  const updatePillsData = (appliedFilters: any, appliedFilterList: any) => {
    const pillsData: any = [];
    if (appliedFilterList && appliedFilterList.length) {
      appliedFilterList.forEach((key: any) => {
        switch (key) {
          case 'orgFilter':
            if (appliedFilters[key]) {
              appliedFilters[key].forEach((item: any) => {
                pillsData.push({
                  name: item.name,
                  label: t('T_ORGANIZATION'),
                  id: key,
                  key: item.id
                  //hideIcon: queryParams.id ? true : false
                });
              });
            }

            break;
          case 'status':
            if (appliedFilters[key]) {
              appliedFilters[key].forEach((item: any) => {
                pillsData.push({
                  name: item?.display,
                  label: t('T_STATUS'),
                  id: key,
                  key: item.key
                  //hideIcon: queryParams.id ? true : false
                });
              });
            }
            break;
          // case 'conditions':
          //   if (appliedFilters[key]) {
          //     appliedFilters[key].forEach((item: any) => {
          //       pillsData.push({
          //         name: item.value,
          //         label: 'Conditions',
          //         id: key,
          //         key: item.key
          //         //hideIcon: queryParams.id ? true : false
          //       });
          //     });
          //   }
          //   break;
          // case 'careTeamFilter':
          //   if (appliedFilters[key]) {
          //     appliedFilters[key].forEach((item: any) => {
          //       pillsData.push({
          //         name: item.name,
          //         label: 'CareTeams',
          //         id: key,
          //         key: item.id
          //         //hideIcon: queryParams.id ? true : false
          //       });
          //     });
          //   }
          //   break;
          case 'intentFilter':
            if (appliedFilters[key]) {
              appliedFilters[key].forEach((item: any) => {
                pillsData.push({
                  name: item?.display,
                  label: t('T_CARE_PLAN_INTENT'),
                  id: key,
                  key: item.key
                  //hideIcon: queryParams.id ? true : false
                });
              });
            }
            break;
          case 'titleFilter':
            if (appliedFilters[key] && appliedFilters[key].length) {
              pillsData.push({
                name: appliedFilters[key],
                label: t('T_TITLE'),
                id: key,
                key: appliedFilters[key]
                //hideIcon: queryParams.id ? true : false
              });
            }
            break;
        }
      });
    }

    return pillsData;
  };

  const updateParamObj = (appliedFilters: any, appliedFilterList: any) => {
    const initialParamObj: any = { ...paramObj };
    const initialFilterList: any = [...filterList];
    initialFilterList.forEach((filterObj: any) => {
      switch (filterObj.id) {
        case 'orgFilter':
          //initialParamObj[filterObj.id] = map(appliedFilters[filterObj.id], 'id').join(',');
          initialParamObj[filterObj.id] = map(appliedFilters[filterObj.id], 'id');
          break;
        case 'status':
        case 'conditions':
        case 'careTeamFilter':
        case 'intentFilter':
          initialParamObj[filterObj.id] = map(appliedFilters[filterObj.id], 'key');
          break;
      }
    });

    return initialParamObj;
  };

  const removeFromPillsFilter = (pillsFilterObj: any) => {
    let initialFilterList: any = [...filterList];
    const initialFilterObj = find(initialFilterList, { id: pillsFilterObj.id });
    if (initialFilterObj.id === 'status' || initialFilterObj.id === 'intentFilter') {
      initialFilterObj.selectedFilter = filter(initialFilterObj.selectedFilter, (obj) => {
        return obj.display !== pillsFilterObj.name;
      });
    }

    // if (initialFilterObj.id === 'conditions') {
    //   initialFilterObj.selectedFilter = filter(initialFilterObj.selectedFilter, (obj) => {
    //     return obj.key !== pillsFilterObj.key;
    //   });
    // }

    // if (initialFilterObj.id === 'careTeamFilter') {
    //   initialFilterObj.selectedFilter = filter(initialFilterObj.selectedFilter, (obj) => {
    //     return obj.id !== pillsFilterObj.key;
    //   });
    // }

    if (initialFilterObj.id === 'orgFilter') {
      initialFilterObj.selectedFilter = filter(initialFilterObj.selectedFilter, (obj) => {
        return obj.name !== pillsFilterObj.name;
      });
    }

    if (initialFilterObj.id === 'titleFilter') {
      initialFilterObj.selectedFilter = [];
    }

    const updatedFilterList = initialFilterList.map((obj: any) => {
      if (obj.id === pillsFilterObj.id) {
        return { ...obj, initialFilterObj };
      } else {
        return { ...obj };
      }
    });
    updateParamObjAfterremovePills(updatedFilterList);
    updatePillsDataAfterRemovePills(updatedFilterList);
    setFilterList(updatedFilterList);
  };

  const updatePillsDataAfterRemovePills = (updatedFilterList: any) => {
    const pillsData: any = [];
    updatedFilterList.forEach((obj: any) => {
      switch (obj.id) {
        case 'orgFilter':
          obj.selectedFilter.forEach((item: any) => {
            pillsData.push({
              name: item.name,
              label: 'Organization',
              id: obj.id,
              key: item.id
              // hideIcon: queryParams.id ? true : false
            });
          });
          break;
        case 'status':
          obj.selectedFilter.forEach((item: any) => {
            pillsData.push({
              name: item.value,
              label: 'Status',
              id: item.id,
              key: item.key
              // hideIcon: queryParams.id ? true : false
            });
          });
          break;
        // case 'conditions':
        //   obj.selectedFilter.forEach((item: any) => {
        //     pillsData.push({
        //       name: item.value,
        //       label: 'Conditions',
        //       id: item.id,
        //       key: item.key
        //       // hideIcon: queryParams.id ? true : false
        //     });
        //   });
        //   break;
        // case 'careTeamFilter':
        //   obj.selectedFilter.forEach((item: any) => {
        //     pillsData.push({
        //       name: item.value,
        //       label: 'CareTeams',
        //       id: item.id,
        //       key: item.id
        //       // hideIcon: queryParams.id ? true : false
        //     });
        //   });
        //   break;
        case 'intentFilter':
          obj.selectedFilter.forEach((item: any) => {
            pillsData.push({
              name: item.value,
              label: 'Intent',
              id: item.id,
              key: item.key
              // hideIcon: queryParams.id ? true : false
            });
          });
          break;
        case 'titleFilter':
          obj.selectedFilter.forEach((item: any) => {
            pillsData.push({
              name: item.value,
              label: 'Title',
              id: item.id,
              key: item.key
              // hideIcon: queryParams.id ? true : false
            });
          });
          break;
      }
    });
    setFilterPillsData(pillsData);
  };

  const saveSelection = () => {
    dispatch(setManifest({ key: 'careplans', value: selectedItems }));
    toggleModal();
  };

  const updateParamObjAfterremovePills = (updatedFilterList: any) => {
    const initialParamObj: any = { ...paramObj };
    updatedFilterList.forEach((obj: any) => {
      initialParamObj[obj.id] = obj.selectedFilter;
      // switch (obj.id) {
      //   case 'orgFilter':
      //     //initialParamObj[obj.id] = map(obj.selectedFilter, 'id').join(',');
      //     initialParamObj[obj.id] = map(obj.selectedFilter, 'id');
      //     break;
      //   case 'statusFilter':
      //     //initialParamObj[obj.id] = map(obj.selectedFilter, 'key').join(',');
      //     initialParamObj[obj.id] = map(obj.selectedFilter, 'key');
      //     break;
      // }
    });
    setParamObj(initialParamObj);
    setIsLoading(true);
    const {
      searchText = '',
      currentPage: filterCurrentPage = AppConstants.DEFAULT_PAGE,
      size: filterSize = type === 'listing'
        ? AppConstants.DEFAULT_PAGE_SIZE
        : AppConstants.MODULE_LIST_DEFAULT_PAGE_SIZE,
      sortName = '',
      sortType = ''
    } = globalFilters['careplan-list'] || {};

    dispatch(
      setTableOptions({
        ['careplan-list']: {
          searchText: searchText,
          currentPage: filterCurrentPage,
          size: filterSize,
          sortName: sortName,
          sortType: sortType,
          tableFilters: initialParamObj
        }
      })
    );
  };

  const refreshTableData = (
    search: string = '',
    page: number,
    size: number,
    sortOrder: string = '',
    sortType: string = '',
    filters: any
  ) => {
    setTableValues(page, size, search, sortOrder, sortType, filters);
  };

  const setTableValues = (
    page: number,
    size: number,
    search: string,
    sortOrder: string = '',
    sortType: string = '',
    filters: any
  ) => {
    if (type === 'migrationPreview') {
      setCurrentActivePage(page * size - size);
    }
    setIsLoading(true);
  };

  const elementSelector = (data: any, step: number) => {
    if (
      type === 'listing' &&
      checkFeaturePermission(AppConstants.FEATURE_KEYS.CAREPLAN_MANAGEMENT_UPDATE, false)
    ) {
      const { id } = data;
      // dispatch(setCareplanData(data)); //Removed Due to inconsistent filter response
      dispatch(setCareplanData({}));
      navigate(RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_VIEW.replace(':id', id));
    } else if (type === 'listing') {
      dispatch(
        setToastData({
          toastMessage: t('T_USER_NO_PERMISSION_TOAST_MESSAGE').replace(
            'Permission',
            t('T_CARE_PLAN_DETAIL')
          ),
          isToastActive: true,
          toastType: 'error'
        })
      );
    }
  };

  const removeSelection = (index: number) => {
    const row: any = rows[index + currentActivePage];
    if (row) {
      const { id } = row;
      dispatch(deleteManifestItem({ key: 'careplans', id }));
    }
  };

  return (
    <LoaderContainer isLoading={isLoading}>
      <TableContainer className={`${type !== 'listing' && '!pb-0'}`}>
        {type === 'listing' ? (
          <MenuButtonsPortal>
            {checkFeaturePermission(
              AppConstants.FEATURE_KEYS.CAREPLAN_MANAGEMENT_CREATE,
              false
            ) && (
              <BiButton
                className={'flex flex-row bg-primary text-white'}
                type="button"
                onClick={() => {
                  navigate(RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_CREATE);
                }}
              >
                <img src={AddCircle} className="mr-2 w-6 text-center"></img>
                {t('T_NEW_CAREPLAN')}
              </BiButton>
            )}
          </MenuButtonsPortal>
        ) : (
          ''
        )}

        <TemplateTable
          isLoading={isLoading}
          tableData={
            type === 'migrationPreview'
              ? rows.slice(
                  currentActivePage,
                  currentActivePage +
                    (globalFilters['careplan-list']
                      ? globalFilters['careplan-list'].size
                      : AppConstants.MODULE_LIST_DEFAULT_PAGE_SIZE)
                )
              : rows
          }
          templateSelector={elementSelector}
          cellSelector={elementSelector}
          onRefreshTableData={refreshTableData}
          isSearchable={false}
          searchPlaceholder={t('T_SEARCH_NAME_CARE_PLAN_TITLE_TEMPLATE') || ''}
          tableHeaders={tableHeaders}
          setTableHeaders={setTableHeaders}
          setCurrentPage={(page: any) => {
            setCurrentActivePage(parseInt(page));
          }}
          currentPage={
            type === 'migrationPreview'
              ? currentActivePage
              : globalFilters?.[`careplan-list-${type}`]?.currentPage
          }
          size={
            type === 'migrationPreview'
              ? AppConstants.MODULE_LIST_DEFAULT_PAGE_SIZE
              : globalFilters?.[`careplan-list-${type}`]?.size?.toString()
          }
          pagingData={paging}
          containerClass="mt-0"
          showDeleteButton={true}
          theme={useTheme().theme}
          isLocalPaginator={type === 'migrationPreview'}
          totalDataLength={rows.length}
          isPaginated={type !== 'migrationPreview'}
          disablePageSize={type !== 'listing'}
          deleteOnClick={removeSelection}
          isFilterable={type !== 'migrationPreview'}
          filtersList={filterList}
          applyFilters={applyFilters}
          pillsData={filterPillsData}
          removeFilter={removeFromPillsFilter}
          defaultPillsLabel={t('T_CONFIG_CATEGORIES_FILTERS_DEFAULT_VIEW')}
          globalFilters={globalFilters}
          tablePage="careplan-list"
          type={type}
          filterWidth={80}
        />
      </TableContainer>
      <Modal
        showCloseIcon={true}
        width="48rem"
        headerText={t('T_DELETE_CONFIRMATION_TITLE')}
        toggleVisiblity={(value: boolean) => {
          setDeletePopup(value);
        }}
        isVisible={deletePopup}
        body={
          <div>
            <div>{t('T_CARE_PLAN_DELETE_PROMPT')}</div>
            <div className="mt-8 flex">
              <BiButton
                className={'ml-auto mr-2 text-primary'}
                type="button"
                onClick={() => {
                  setDeletePopup(false);
                }}
              >
                {t('T_CANCEL')}
              </BiButton>
              <BiButton
                className={'bg-primary text-white'}
                type="button"
                onClick={() => {
                  deleteCareplanAPI();
                  setDeletePopup(false);
                }}
              >
                {t('T_DELETE')}
              </BiButton>
            </div>
          </div>
        }
        headerLeftIcon={<ExclamationIcon className="h-7.5 w-6" />}
      />
      {type === 'migrationSelection' ? (
        <div className="w-full">
          <ActionButtons
            primaryButtonLabel={t('T_SAVE')}
            primaryButtonAction={saveSelection}
            primaryButtonDisabled={!selectedItems.length}
            secondaryButtonLabel={t('T_CANCEL')}
            secondaryButtonAction={toggleModal}
            primaryButtonWidth="full"
            primaryButtonClass={'px-4'}
          />
        </div>
      ) : (
        ''
      )}
    </LoaderContainer>
  );
};

export default CarePlanTemplateList;
